// Multi-modal notification manager for event alerts
class NotificationManager {
    constructor() {
        this.audioContext = null;
        this.originalTitle = document.title;
        this.titleInterval = null;
        this.settings = {
            sound: true,
            browserNotification: true,
            titleFlash: true,
            favicon: true
        };
        
        this.audioInitialized = false;
        this.initBrowserNotifications();
    }

    // Initialize browser notification permission
    async initBrowserNotifications() {
        if ('Notification' in window && Notification.permission === 'default') {
            try {
                await Notification.requestPermission();
            } catch (error) {
                console.warn('Failed to request notification permission:', error);
            }
        }
    }

    // Initialize audio context
    initAudioContext() {
        if (!this.audioContext) {
            try {
                this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
                console.log('AudioContext created, state:', this.audioContext.state);
            } catch (error) {
                console.error('Failed to create AudioContext:', error);
                return null;
            }
        }
        
        return this.audioContext;
    }

    // Play sound notification
    async playSound() {
        if (!this.settings.sound) {
            console.log('Sound notifications disabled in settings');
            return;
        }
        
        try {
            const audioContext = this.initAudioContext();
            
            if (!audioContext) {
                console.error('AudioContext not available');
                return;
            }
            
            // Ensure audio context is running
            if (audioContext.state === 'suspended') {
                console.log('Resuming suspended AudioContext...');
                await audioContext.resume();
            }
            
            console.log('Playing notification sound, AudioContext state:', audioContext.state);
            
            // Create pleasant chime sequence
            this.playTone(audioContext, 800, 0, 0.15);    // C5
            this.playTone(audioContext, 1000, 0.2, 0.15); // C6
            this.playTone(audioContext, 1200, 0.4, 0.2);  // E6
            
            this.audioInitialized = true;
            
        } catch (error) {
            console.error('Failed to play sound:', error);
        }
    }

    // Generate audio tone
    playTone(audioContext, frequency, startTime, duration) {
        try {
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();
            
            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);
            
            oscillator.type = 'sine';
            oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime + startTime);
            
            const now = audioContext.currentTime + startTime;
            gainNode.gain.setValueAtTime(0, now);
            gainNode.gain.linearRampToValueAtTime(0.3, now + 0.01);
            gainNode.gain.linearRampToValueAtTime(0.1, now + 0.1);
            gainNode.gain.setValueAtTime(0.1, now + duration - 0.05);
            gainNode.gain.linearRampToValueAtTime(0, now + duration);
            
            oscillator.start(now);
            oscillator.stop(now + duration);
        } catch (error) {
            console.error('Error in playTone:', error);
        }
    }

    // Show browser notification
    showBrowserNotification(eventCount) {
        if (!this.settings.browserNotification) return;
        if (!('Notification' in window) || Notification.permission !== 'granted') {
            console.log('Browser notifications not available or not granted');
            return;
        }
        
        try {
            const notification = new Notification('New Event Alert!', {
                body: `${eventCount} new event${eventCount > 1 ? 's' : ''} detected`,
                icon: '/favicon.ico',
                badge: '/favicon.ico',
                tag: 'event-alert',
                requireInteraction: false,
                silent: false
            });
            
            // Auto-close after 5 seconds
            setTimeout(() => {
                notification.close();
            }, 5000);
            
            // Handle click to focus window
            notification.onclick = () => {
                window.focus();
                notification.close();
            };
            
        } catch (error) {
            console.warn('Failed to show browser notification:', error);
        }
    }

    // Flash page title
    flashTitle() {
        if (!this.settings.titleFlash) return;
        
        this.stopTitleFlash(); // Stop any existing flash
        
        let isAlert = false;
        this.titleInterval = setInterval(() => {
            document.title = isAlert ? this.originalTitle : '🔔 NEW EVENT!';
            isAlert = !isAlert;
        }, 1000);
        
        // Stop flashing after 30 seconds
        setTimeout(() => {
            this.stopTitleFlash();
        }, 30000);
    }

    // Stop title flashing
    stopTitleFlash() {
        if (this.titleInterval) {
            clearInterval(this.titleInterval);
            this.titleInterval = null;
            document.title = this.originalTitle;
        }
    }

    // Change favicon to alert state
    changeFavicon(isAlert = true) {
        if (!this.settings.favicon) return;
        
        try {
            const link = document.querySelector("link[rel~='icon']") || document.createElement('link');
            link.rel = 'icon';
            
            if (isAlert) {
                // Create red alert favicon using canvas
                const canvas = document.createElement('canvas');
                canvas.width = 16;
                canvas.height = 16;
                const ctx = canvas.getContext('2d');
                
                // Red circle
                ctx.fillStyle = '#ff4444';
                ctx.beginPath();
                ctx.arc(8, 8, 7, 0, 2 * Math.PI);
                ctx.fill();
                
                // White exclamation mark
                ctx.fillStyle = 'white';
                ctx.font = 'bold 12px Arial';
                ctx.textAlign = 'center';
                ctx.fillText('!', 8, 12);
                
                link.href = canvas.toDataURL();
            } else {
                // Reset to original favicon
                link.href = '/favicon.ico';
            }
            
            document.head.appendChild(link);
        } catch (error) {
            console.warn('Failed to change favicon:', error);
        }
    }

    // Main notification trigger
    async notifyNewEvents(eventCount) {
        console.log(`Triggering notifications for ${eventCount} new event(s)`);
        console.log('Current settings:', this.settings);
        
        // Trigger all notification modes
        await this.playSound();
        this.showBrowserNotification(eventCount);
        this.flashTitle();
        this.changeFavicon(true);
        
        // Reset favicon after 30 seconds
        setTimeout(() => {
            this.changeFavicon(false);
        }, 30000);
    }

    // User settings management
    updateSettings(newSettings) {
        this.settings = { ...this.settings, ...newSettings };
        localStorage.setItem('notificationSettings', JSON.stringify(this.settings));
        console.log('Notification settings updated:', this.settings);
    }

    // Load settings from localStorage
    loadSettings() {
        try {
            const saved = localStorage.getItem('notificationSettings');
            if (saved) {
                this.settings = { ...this.settings, ...JSON.parse(saved) };
                console.log('Loaded notification settings:', this.settings);
            } else {
                console.log('No saved settings, using defaults:', this.settings);
            }
        } catch (error) {
            console.warn('Failed to load notification settings:', error);
        }
    }

    // Get current settings
    getSettings() {
        return { ...this.settings };
    }

    // Initialize on page visibility change
    handleVisibilityChange() {
        if (document.visibilityState === 'visible') {
            // Page became visible - stop title flashing and reset favicon
            this.stopTitleFlash();
            this.changeFavicon(false);
        }
    }

    // Setup event listeners
    init() {
        this.loadSettings();
        
        // Listen for visibility changes
        document.addEventListener('visibilitychange', () => {
            this.handleVisibilityChange();
        });
        
        // Enable audio context on first user interaction
        const enableAudio = async () => {
            console.log('User interaction detected, initializing audio...');
            const audioContext = this.initAudioContext();
            if (audioContext && audioContext.state === 'suspended') {
                await audioContext.resume();
                console.log('AudioContext resumed on user interaction');
            }
        };
        
        // Add multiple event listeners to ensure audio gets initialized
        document.addEventListener('click', enableAudio, { once: true });
        document.addEventListener('keydown', enableAudio, { once: true });
        document.addEventListener('touchstart', enableAudio, { once: true });
        
        console.log('NotificationManager initialized');
    }
}

// Create singleton instance
const notificationManager = new NotificationManager();
notificationManager.init();

export default notificationManager;